import { useState } from 'react';
import { ArrowDownToLine, ArrowUpFromLine, Lock, Plus, SlidersHorizontal } from 'lucide-react';
import type { CashRegisterDetailDto } from '@barbeiropro/contracts';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Button } from '@/components/ui/button';
import { fmtDateTime, money } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { useCurrentRegister } from '../api';
import { KIND_LABEL, KIND_ORDER } from '../labels';
import { CloseRegisterModal } from './close-register-modal';
import { MovementModal, type MovementKind } from './movement-modal';
import { MovementsTable } from './movements-table';
import { OpenRegisterModal } from './open-register-modal';

function Totals({ r }: { r: CashRegisterDetailDto }) {
  const received = r.totalsByMethod.reduce((s, t) => s + t.paymentsCents, 0);
  const rows = KIND_ORDER.map((k) => r.totalsByMethod.find((t) => t.methodKind === k) ?? { methodKind: k, paymentsCents: 0, refundsCents: 0, otherCents: 0, netCents: 0 });
  return (
    <section aria-label="Totais do caixa" className="grid gap-6 rounded-lg border bg-card p-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <div className="grid content-start gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Dinheiro esperado na gaveta</p>
          <p className="font-display tabular text-5xl leading-none">{money(r.expectedCashNowCents)}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Total recebido</p>
          <p className="tabular text-2xl font-semibold">{money(received)}</p>
          <p className="text-xs text-muted-foreground tabular">
            {r.paymentsCount} {r.paymentsCount === 1 ? 'recebimento' : 'recebimentos'} · abertura de {money(r.openingAmountCents)}
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="pb-2 text-left text-sm text-muted-foreground">Por método de pagamento</caption>
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th scope="col" className="py-1.5 text-left font-semibold">Método</th>
              <th scope="col" className="py-1.5 text-right font-semibold">Recebido</th>
              <th scope="col" className="py-1.5 text-right font-semibold">Estornos</th>
              <th scope="col" className="py-1.5 text-right font-semibold">Líquido</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.methodKind} className="border-b last:border-0">
                <th scope="row" className="py-1.5 text-left font-normal">{KIND_LABEL[t.methodKind]}</th>
                <td className="tabular py-1.5 text-right">{money(t.paymentsCents)}</td>
                <td className="tabular py-1.5 text-right text-muted-foreground">{money(Math.abs(t.refundsCents))}</td>
                <td className="tabular py-1.5 text-right font-medium">{money(t.netCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function CurrentRegister({ branchId, branchName }: { branchId: string; branchName?: string }) {
  const q = useCurrentRegister(branchId);
  const tz = useTimezone();
  const { can } = useSession();
  const [openModal, setOpenModal] = useState(false);
  const [movement, setMovement] = useState<MovementKind | null>(null);
  const [closing, setClosing] = useState(false);

  if (q.isLoading) return <LoadingState label="Carregando caixa…" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const reg = q.data?.register ?? null;

  if (!reg) {
    return (
      <>
        <EmptyState
          title="Nenhum caixa aberto nesta unidade"
          description="Abra o caixa para registrar recebimentos de atendimentos e assinaturas."
          action={
            <Button onClick={() => setOpenModal(true)}>
              <Plus className="size-4" aria-hidden /> Abrir caixa
            </Button>
          }
        />
        <OpenRegisterModal open={openModal} onOpenChange={setOpenModal} branchId={branchId} branchName={branchName} />
      </>
    );
  }

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Aberto em <span className="tabular">{fmtDateTime(reg.openedAt, tz)}</span> por {reg.openedByName ?? 'usuário'}
        </p>
        {can('cash.manage') && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setMovement('withdrawal')}>
              <ArrowUpFromLine className="size-4" aria-hidden /> Sangria
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMovement('deposit')}>
              <ArrowDownToLine className="size-4" aria-hidden /> Suprimento
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMovement('adjustment')}>
              <SlidersHorizontal className="size-4" aria-hidden /> Ajuste
            </Button>
            <Button size="sm" onClick={() => setClosing(true)}>
              <Lock className="size-4" aria-hidden /> Fechar caixa
            </Button>
          </div>
        )}
      </div>
      <Totals r={reg} />
      <section aria-label="Movimentos do caixa" className="grid gap-2">
        <h2 className="text-base font-semibold">Movimentos</h2>
        <MovementsTable movements={reg.movements} />
      </section>
      <MovementModal kind={movement} registerId={reg.id} onClose={() => setMovement(null)} />
      {closing && <CloseRegisterModal register={reg} open onOpenChange={setClosing} />}
    </div>
  );
}
